import URL from '@core/scripts/helper/url';
import Logger from '@core/scripts/components/logger';
import ConsentService from './ConsentService';

const HURRA_SCRIPT_ID = 'hurra-init';

class HurraScriptService {
    /**
     * Loads the Hurra tracking script into the DOM.
     *
     * @param {Object} options - Load options
     * @param {string} options.hurraInstanceId - Hurra instance ID
     * @param {string} [options.hurraScriptUrl] - Optional custom Hurra script URL
     * @param {boolean} options.attOptInStatus - iOS ATT permission status
     * @param {Function} [options.onLoad] - Script load callback
     * @param {Function} [options.onError] - Script error callback
     * @returns {boolean} True when script append is attempted, false otherwise
     */
    static loadScript({hurraInstanceId, hurraScriptUrl, attOptInStatus, onLoad, onError}) {
        if (!hurraInstanceId) {
            Logger.warn('Cannot load Hurra script: missing hurraInstanceId');
            return false;
        }

        if (document.getElementById(HURRA_SCRIPT_ID)) {
            Logger.debug('Hurra script already loaded');
            return false;
        }

        const script = document.createElement('script');
        script.id = HURRA_SCRIPT_ID;
        script.async = true;
        script.type = 'text/javascript';

        const baseUrl = HurraScriptService.resolveBaseUrl(hurraScriptUrl);
        const scriptUrl = new window.URL(baseUrl);
        scriptUrl.pathname = `${scriptUrl.pathname.replace(/\/+$/, '')}/oti.js`;
        scriptUrl.searchParams.set('cid', hurraInstanceId);
        scriptUrl.searchParams.set('skip_track', '1');

        const gdprAtt = ConsentService.getGdprAttCookie();
        if (gdprAtt === undefined && attOptInStatus === false) {
            scriptUrl.searchParams.set('nt', '1');
            scriptUrl.searchParams.set('gdpr_att', '0');
        }

        script.src = scriptUrl.toString();
        script.onload = () => onLoad?.();
        script.onerror = (error) => onError?.(error);

        document.body.appendChild(script);
        return true;
    }

    static resolveBaseUrl(hurraScriptUrl) {
        const candidate = (hurraScriptUrl || URL.toAbsolute('hc_scripts')).trim();
        return new window.URL(candidate, window.location.origin).toString().replace(/\/+$/, '');
    }

    /**
     * Loads the Hurra script with retry logic.
     *
     * @param {Object} options - Load options
     * @param {string} options.hurraInstanceId - Hurra instance ID
     * @param {string} [options.hurraScriptUrl] - Optional custom Hurra script URL
     * @param {boolean} options.attOptInStatus - iOS ATT permission status
     * @param {number} [options.maxAttempts=3] - Max retry attempts
     * @param {number} [options.retryDelayMs=1000] - Delay between retries
     * @returns {Promise<boolean>} True if loaded, false if failed after retries
     */
    static loadWithRetry({hurraInstanceId, hurraScriptUrl, attOptInStatus, maxAttempts = 3, retryDelayMs = 1000}) {
        let attempt = 0;

        return new Promise((resolve) => {
            const tryLoad = () => {
                attempt += 1;

                const appended = HurraScriptService.loadScript({
                    hurraInstanceId,
                    hurraScriptUrl,
                    attOptInStatus,
                    onLoad: () => resolve(true),
                    onError: (error) => {
                        Logger.error(`Failed to load Hurra script (attempt ${attempt}/${maxAttempts})`, error);

                        const failedScript = document.getElementById(HURRA_SCRIPT_ID);
                        if (failedScript) {
                            failedScript.remove();
                        }

                        if (attempt < maxAttempts) {
                            Logger.info('Retrying Hurra script load in 1 second...');
                            setTimeout(tryLoad, retryDelayMs);
                        } else {
                            Logger.warn('Max retry attempts reached for Hurra script. Continuing with fallback parameters only.');
                            resolve(false);
                        }
                    }
                });

                if (!appended) {
                    // Script already present or missing ID - treat as loaded
                    resolve(true);
                }
            };

            tryLoad();
        });
    }
}

export default HurraScriptService;

